/**
 * Web Push subscription helpers.
 * Consumers: push-notification-actions.js
 */

export function isPushSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export async function requestNotificationPermission() {
    if (!isPushSupported()) return 'unsupported';
    return await Notification.requestPermission();
}

export async function getExistingSubscription() {
    if (!isPushSupported()) return null;
    try {
        const reg = await Promise.race([
            navigator.serviceWorker.ready,
            new Promise((_, reject) =>
                setTimeout(() => reject(new Error('SW ready timeout')), 5000)
            )
        ]);
        return await reg.pushManager.getSubscription();
    } catch {
        return null;
    }
}

export async function subscribeUserToPush(vapidPublicKey) {
    // Ensure service worker is registered
    let reg = await navigator.serviceWorker.getRegistration('/');
    if (!reg) {
        reg = await navigator.serviceWorker.register('/serviceworker.js?v=2.6.0');
    }

    // Wait for SW to activate with a 10s timeout — navigator.serviceWorker.ready
    // hangs forever if no SW ever activates, so we must guard with a timeout.
    const activeReg = await Promise.race([
        navigator.serviceWorker.ready,
        new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Service worker did not activate in time')), 10000)
        )
    ]);

    const subscription = await activeReg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
    });
    return subscription;
}

export async function unsubscribeUserFromPush() {
    const subscription = await getExistingSubscription();
    if (subscription) await subscription.unsubscribe();
}

function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}
